import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ProfileContext = createContext(null);
const LS_KEY = "harmonix_profile_id";

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem(LS_KEY));
  const [ready, setReady] = useState(false);

  const setActiveId = useCallback((id) => {
    if (id) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
    setActiveIdState(id);
  }, []);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/profiles");
      setProfiles(data);
      // Ensure a valid active profile.
      if (!data.find((p) => p.id === activeId)) {
        const def = data.find((p) => p.is_default) || data[0];
        if (def) setActiveId(def.id);
      }
    } finally {
      setReady(true);
    }
  }, [user, activeId, setActiveId]);

  useEffect(() => {
    if (user) refresh();
    else {
      setProfiles([]);
      setReady(false);
    }
  }, [user, refresh]);

  const active = profiles.find((p) => p.id === activeId) || null;

  return (
    <ProfileContext.Provider
      value={{ profiles, activeId, active, ready, setActiveId, refresh }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export const useProfile = () => useContext(ProfileContext);
